import React, { useMemo, useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    IconButton,
    Tooltip,
    Select,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Button,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuGroup,
    MenuDivider
} from "@chakra-ui/react";
import {
    ArrowRightIcon,
    ArrowLeftIcon,
    ChevronRightIcon,
    ChevronLeftIcon
} from "@chakra-ui/icons";
import {
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from "react-table";
import Card from "components/card/Card";
import { RiExpandUpDownFill, RiArrowUpSFill, RiArrowDownSFill } from "react-icons/ri";
import { stringIsNullOrEmpty } from "common/util";
import { showCustomDialog } from "../application/action/app_action";
import ApiEngine from "../common/api-engine";
import { ApiKey } from "../common/constant";
import { constructQueryString } from "common/util";

/// <summary>
/// Author: -
/// </summary>
export default function CustomTable({
    title,
    columnsData = [],
    tableData = [],
    apiUrl = '',
    objFilter = {},
    requestMethod = ApiKey._API_GET,
    customFetchAction,
    RowAccordionContent = null,
    RowAccordionContentItem = null,
    SortMenus = null
}) {
    var _dispatch = useDispatch();
    const { t } = useTranslation();
    const _PAGE_SIZE_OPTION = [10, 20, 50, 100, 500];
    const columns = useMemo(() => columnsData, [columnsData]);
    const [data, setData] = useState(tableData);
    const [pageSize, setPageSize] = useState(_PAGE_SIZE_OPTION[0]);
    const [total, setTotal] = useState(tableData.length);
    const [apiQuery, setApiQuery] = useState("");
    const [sortBy, setSortBy] = useState(null);

    const tableInstance = useTable(
        {
            columns,
            data,
            manualPagination: !stringIsNullOrEmpty(apiQuery),
            pageCount: Math.ceil(total / pageSize),
            initialState: {
                pageIndex: 0,
                pageSize: pageSize,
            },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        state: { pageIndex }
    } = tableInstance;
    const textColor = useColorModeValue("black", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const brandColor = useColorModeValue("brand.100", "secondaryGray.800");
    const headerTextColor = useColorModeValue("black", "white");
    const buttonIconColor = useColorModeValue("brand.900", "white");

    /// <summary>
    /// Author: -
    /// </summary>
    useEffect(() => {
        if (!stringIsNullOrEmpty(apiUrl)) {
            setApiQuery(apiUrl);
        }
    }, [apiUrl]);

    /// <summary>
    /// Author: -
    /// </summary>
    useEffect(() => {
        fetchData();
    }, [apiQuery, pageSize, sortBy, objFilter]);

    /// <summary>
    /// Author : -
    /// </summary>
    useEffect(() => {
        if (tableData.length > 0) {
            setTotal(tableData.length);
            setData(tableData);
            gotoPage(0);
        }
    }, [tableData]);

    /// <summary>
    /// Author: -
    /// </summary>
    async function fetchData(pageNo = 1) {
        try {
            if (!stringIsNullOrEmpty(apiQuery)) {
                var responseJson = null;
                objFilter["pageIndex"] = pageNo;
                objFilter["pageSize"] = pageSize;

                if (sortBy != null) {
                    objFilter['sortById'] = sortBy?.selected?.sortById;
                    objFilter['ascending'] = sortBy?.selected?.ascending;
                }

                if (requestMethod == ApiKey._API_GET) {
                    responseJson = await ApiEngine.get(constructQueryString(apiQuery, objFilter));
                }
                else {
                    responseJson = await ApiEngine.post(apiQuery, objFilter);
                }

                if (!responseJson[ApiKey._API_SUCCESS_KEY]) {
                    throw responseJson[ApiKey._API_MESSAGE_KEY];
                }

                if (customFetchAction != null) {
                    customFetchAction(responseJson);
                }

                setTotal(responseJson[ApiKey._API_COUNT_KEY] ?? responseJson[ApiKey._API_DATA_KEY].length);
                setData(responseJson[ApiKey._API_DATA_KEY]);
                gotoPage(pageNo - 1);
            }
            else {
                gotoPage(pageNo - 1);
            }
        }
        catch (err) {
            _dispatch(showCustomDialog({ success: false, content: err }));
        }
    }

    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            mb='20px'
            overflowX={{ sm: "scroll", lg: "scroll" }}>
            <Flex justify='space-between' mb='20px' align='center'>
                <Text
                    color={textColor}
                    fontSize='12px'
                    fontWeight='700'
                    lineHeight='100%'>
                    {!stringIsNullOrEmpty(title) && t(title)}
                </Text>
                <Flex>
                    {
                        SortMenus != null && SortMenus.length > 0 &&
                        <Flex className="sort-by-menu-container" mr={'5px'}>
                            <Menu isLazy={true} boundary={'scrollParent'}>
                                <MenuButton as={Button}
                                    colorScheme={'brand'}
                                    variant={'brand'}
                                >
                                    {sortBy != null ? t(sortBy.category) + " (" + t(sortBy.selected.label) + ")" : t('SORT_BY')}
                                </MenuButton>
                                <MenuList>
                                    {
                                        SortMenus.map(menu =>
                                            <>
                                                <MenuGroup title={t(menu.category)}>
                                                    {
                                                        menu.options.map(option =>
                                                            <MenuItem onClick={() => {
                                                                setSortBy({
                                                                    category: menu.category,
                                                                    selected: option
                                                                });
                                                            }}>{t(option.label)}</MenuItem>
                                                        )
                                                    }
                                                </MenuGroup>
                                                <MenuDivider />
                                            </>
                                        )
                                    }
                                </MenuList>
                            </Menu>
                        </Flex>
                    }
                    {
                        data.length > 0 &&
                        <Select
                            fontSize={'12px'}
                            w={32}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                            }}
                        >
                            {_PAGE_SIZE_OPTION.map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {`${t('SHOW')} ${pageSize}`}
                                </option>
                            ))}
                        </Select>
                    }
                </Flex>
            </Flex>
            {
                <>
                    {
                        RowAccordionContent == null ?
                            <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px' className="custom-table">
                                <Thead>
                                    {headerGroups.map((headerGroup, index) => (
                                        <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                                            {headerGroup.headers.map((column, index) => (
                                                <Th
                                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                                    key={index}
                                                    borderColor={borderColor}
                                                    width={column.headerWidth}
                                                    className={column.headerClassName}
                                                    style={{ cursor: 'pointer' }}>
                                                    <Flex
                                                        justify='space-between'
                                                        align='center'
                                                        fontSize={{ sm: "12px", lg: "12px" }}
                                                        color={headerTextColor}>
                                                        {(column.customHeader != null ? column.render("customHeader") : t(column.render("Header")))}
                                                        {!column.disableSortBy && (column.isSorted ? (column.isSortedDesc ? <RiArrowDownSFill /> : <RiArrowUpSFill />) : <RiExpandUpDownFill />)}
                                                    </Flex>
                                                </Th>
                                            ))}
                                        </Tr>
                                    ))}
                                </Thead>
                                <Tbody {...getTableBodyProps()}>
                                    {page.map((row, index) => {
                                        prepareRow(row);

                                        return (
                                            <Tr {...row.getRowProps()} key={index} _hover={{ background: brandColor }}>
                                                {row.cells.map((cell, index) => {
                                                    return (
                                                        <Td
                                                            {...cell.getCellProps()}
                                                            key={index}
                                                            fontSize={{ sm: "13px" }}
                                                            borderColor='transparent'>
                                                            <Text color={textColor} fontSize='13px' fontWeight='500'>
                                                                {cell.render('Cell')}
                                                            </Text>
                                                        </Td>
                                                    );
                                                })}
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table> :
                            <Accordion allowToggle>
                                <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px' className="custom-table">
                                    <Thead>
                                        {headerGroups.map((headerGroup, index) => (
                                            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                                                <Th
                                                    key={-1}
                                                    borderColor={borderColor}
                                                >
                                                </Th>
                                                {headerGroup.headers.map((column, index) => (
                                                    <Th
                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        key={index}
                                                        borderColor={borderColor}
                                                        className={column.headerClassName}
                                                        width={column.headerWidth}
                                                        style={{ cursor: 'pointer' }}>
                                                        <Flex
                                                            justify='space-between'
                                                            align='center'
                                                            fontSize={{ sm: "12px", lg: "12px" }}
                                                            color={headerTextColor}>
                                                            {(column.customHeader != null ? column.render("customHeader") : t(column.render("Header")))}
                                                            {!column.disableSortBy && (column.isSorted ? (column.isSortedDesc ? <RiArrowDownSFill /> : <RiArrowUpSFill />) : <RiExpandUpDownFill />)}
                                                        </Flex>
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody {...getTableBodyProps()}>
                                        {page.map((row, index) => {
                                            prepareRow(row);

                                            return (
                                                <AccordionItem as={Fragment}>
                                                    <Tr {...row.getRowProps()} key={index} _hover={{ background: brandColor }}>
                                                        <Td>
                                                            <AccordionButton p={0}>
                                                                <AccordionIcon />
                                                            </AccordionButton>
                                                        </Td>
                                                        {row.cells.map((cell, index) => {
                                                            return (
                                                                <Td
                                                                    {...cell.getCellProps()}
                                                                    key={index}
                                                                    fontSize={{ sm: "13px" }}
                                                                    borderColor='transparent'>
                                                                    <Text color={textColor} fontSize='13px' fontWeight='500'>
                                                                        {cell.render('Cell')}
                                                                    </Text>
                                                                </Td>
                                                            );
                                                        })}
                                                    </Tr>
                                                    <Tr>
                                                        <Td colSpan={row.cells.length + 1}>
                                                            <AccordionPanel p={0}>
                                                                <RowAccordionContent
                                                                    item={RowAccordionContentItem ? row.original[RowAccordionContentItem] : row.original}
                                                                    extraInfo={row.original}
                                                                    onRefresh={() => setApiQuery(`${apiQuery}${apiQuery.indexOf('?') > -1 ? '&' : '?'}v=${new Date()}`)} />
                                                            </AccordionPanel>
                                                        </Td>
                                                    </Tr>
                                                </AccordionItem>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </Accordion>
                    }
                </>
            }
            {
                data.length > 0 &&
                <Flex justifyContent="space-between" m={4} alignItems="center">
                    <Flex>
                        <Tooltip label={t('FIRST_PAGE')}>
                            <IconButton
                                onClick={() => fetchData()}
                                isDisabled={!canPreviousPage}
                                icon={<ArrowLeftIcon h={3} w={3} />}
                                mr={4}
                            />
                        </Tooltip>
                        <Tooltip label={t('PREVIOUS_PAGE')}>
                            <IconButton
                                onClick={() => fetchData(pageIndex)}
                                isDisabled={!canPreviousPage}
                                icon={<ChevronLeftIcon h={6} w={6} />}
                            />
                        </Tooltip>
                    </Flex>

                    <Flex alignItems="center">
                        <Text fontSize={'12px'} flexShrink="0" mr={8}>
                            {t('PAGE') + " "}
                            <Text fontWeight="bold" as="span">
                                {pageIndex + 1}
                            </Text>{" "}
                            {t('OF') + " "}
                            <Text fontWeight="bold" as="span">
                                {pageOptions.length}
                            </Text>
                        </Text>
                        <Text fontSize={'12px'} flexShrink="0">{t('GO_TO_PAGE')}:</Text>{" "}
                        <NumberInput
                            size={'sm'}
                            ml={2}
                            mr={8}
                            w={28}
                            min={1}
                            max={pageOptions.length}
                            onChange={(value) => {
                                fetchData(value);
                            }}
                            defaultValue={pageIndex + 1}
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </Flex>

                    <Flex>
                        <Tooltip label={t('NEXT_PAGE')}>
                            <IconButton
                                onClick={() => fetchData(pageIndex + 2)}
                                isDisabled={!canNextPage}
                                icon={<ChevronRightIcon h={6} w={6} />}
                            />
                        </Tooltip>
                        <Tooltip label={t('LAST_PAGE')}>
                            <IconButton
                                onClick={() => fetchData(pageCount)}
                                isDisabled={!canNextPage}
                                icon={<ArrowRightIcon h={3} w={3} />}
                                ml={4}
                            />
                        </Tooltip>
                    </Flex>
                </Flex>
            }
        </Card >
    );
}
